import React, { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from 'helpers/css';
import { TooltipReactTooltip } from 'components/Tooltip';
import { get, isString } from 'lodash';
import NewDesign from '..';
import useToggleDialog from 'hooks/useToggleDialog';
import DialogShowLongContent from 'components/Dialogs/DialogShowLongContent';
import { isDefine } from 'helpers';

const truncateText = (text, countChar) => {
  if (isString(text)) {
    return text.length > countChar ? text.substring(0, countChar) + '...' : text;
  }

  return text;
};

//! README: If you want truncate please use showMaxChar + options, all others Deprecated
/**
 * @param {Object} props
 * @param {any} props.children
 * @param {"span" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "i" | "label" | "b" | "div"} props.component
 * @param {"link" | "subtitle1" | "subtitle2"} props.variant
 * @param {boolean} props.isTruncate
 * @param {number} props.lineTruncate
 * @param {number} props.showMaxChar
 * @param {{ truncateByTooltip: boolean }} props.options
 * @returns
 */
const Typography = (props) => {
  //! State
  const {
    children,
    component: Component = 'span',
    variant,
    isTruncate,
    lineTruncate,
    showMaxChar,
    options,
    ...restProps
  } = props;
  const textRef = useRef(null);
  const [seeMore, setSeeMore] = useState(false);
  const [openViewLongContent, toggleViewLongContent, shouldRenderViewLongContent] =
    useToggleDialog();
  const [isClamped, setIsClamped] = useState(false);
  const { truncateByTooltip } = options || {};

  //! Function
  useLayoutEffect(() => {
    if (isTruncate) {
      const intervalId = setInterval(() => {
        const el = textRef.current;
        if (!el) {
          return;
        }

        const truncated = el.scrollHeight > el.clientHeight;
        setIsClamped(truncated);
        clearInterval(intervalId);
      }, 0);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [isTruncate, children]);

  const onToggle = () => {
    setSeeMore((prev) => !prev);
  };

  const className = useMemo(() => {
    const mappingVariant = {
      link: 'tw-cursor-pointer tw-text-btPrimary hover:tw-underline',
      subtitle1: 'subtitle1',
      subtitle2: 'tw-text-[13px] tw-font-[400] tw-leading-[1.66] tw-text-muted-foreground',
    };

    return cn(get(mappingVariant, variant), restProps.className);
  }, [restProps.className, variant]);

  const sharedProps = useMemo(() => {
    return {
      'tracking-el': '1',
    };
  }, []);

  const truncateLineClampCls = useMemo(() => {
    if (!isTruncate || seeMore) {
      return '';
    }

    const mappingLine = {
      1: 'tw-line-clamp-1',
      2: 'tw-line-clamp-2',
      3: 'tw-line-clamp-3',
      4: 'tw-line-clamp-4',
    };
    return mappingLine?.[lineTruncate] || 'tw-line-clamp-2';
  }, [isTruncate, lineTruncate, seeMore]);

  //! Render
  const renderButtonSeeMore = () => {
    if (isDefine(showMaxChar)) {
      if (truncateByTooltip) {
        return null;
      }

      if (isString(children) && `${children}`.length <= showMaxChar) {
        return null;
      }
    }

    if (!isDefine(showMaxChar) && (!isTruncate || (isTruncate && !isClamped))) {
      return null;
    }

    return (
      <span className='link-color is-hover pl-1' onClick={onToggle}>
        {seeMore ? 'See less' : 'See more'}
      </span>
    );
  };

  const renderContent = () => {
    if (isDefine(showMaxChar) && !seeMore) {
      return truncateText(children, showMaxChar);
    }

    return children;
  };

  const Wrapper = truncateByTooltip ? TooltipReactTooltip : Fragment;
  const wrapperProps = useMemo(() => {
    return truncateByTooltip ? { messageToolTip: children } : undefined;
  }, [truncateByTooltip]);

  return (
    <Fragment>
      {shouldRenderViewLongContent && (
        <DialogShowLongContent
          isOpen={openViewLongContent}
          toggle={toggleViewLongContent}
          content={children}
        />
      )}

      <Wrapper {...wrapperProps}>
        <Component
          ref={textRef}
          {...sharedProps}
          {...restProps}
          className={cn(className, truncateLineClampCls)}
        >
          {renderContent()}
        </Component>

        {renderButtonSeeMore()}
      </Wrapper>
    </Fragment>
  );
};

export default Typography;
